const env = import.meta.env

const TRUE_VALUES = new Set(['1', 'true', 'yes'])

function normalizeEnvValue(value: unknown): string {
  return typeof value === 'string' ? value.trim() : ''
}

function stripTrailingSlash(value: string): string {
  return value.replace(/\/+$/, '')
}

function withoutApiSuffix(value: string): string {
  return stripTrailingSlash(value).replace(/\/api$/i, '')
}

function withApiSuffix(value: string): string {
  const normalized = stripTrailingSlash(value)
  return /\/api$/i.test(normalized) ? normalized : `${normalized}/api`
}

export const frontendRuntimeMode = normalizeEnvValue(env.MODE) || 'development'
export const isFrontendTestMode = frontendRuntimeMode === 'test'
export const isFrontendDevelopmentMode = Boolean(env.DEV)
export const isFrontendReleaseLikeMode = !isFrontendDevelopmentMode && !isFrontendTestMode

export function isEnvFlagEnabled(value: unknown): boolean {
  return TRUE_VALUES.has(normalizeEnvValue(value).toLowerCase())
}

export function assertFrontendRuntimeConfig(condition: unknown, message: string): asserts condition {
  if (!condition) {
    throw new Error(`[frontend-config] ${message}`)
  }
}

export function getApiBaseUrl(): string {
  const explicitApiBase = normalizeEnvValue(env.VITE_API_BASE_URL)
  const legacyApiUrl = normalizeEnvValue((env as any).VITE_API_URL)

  if (explicitApiBase) {
    return explicitApiBase.startsWith('http') ? withApiSuffix(explicitApiBase) : explicitApiBase
  }

  if (legacyApiUrl) {
    return legacyApiUrl.startsWith('http') ? withApiSuffix(legacyApiUrl) : legacyApiUrl
  }

  if (isFrontendDevelopmentMode || isFrontendTestMode) {
    return '/api'
  }

  assertFrontendRuntimeConfig(false, 'VITE_API_BASE_URL is required outside development/test')
}

export function getApiOriginUrl(): string {
  const explicitOrigin = normalizeEnvValue((env as any).VITE_API_ORIGIN)
  const explicitApiBase = normalizeEnvValue(env.VITE_API_BASE_URL)
  const legacyApiUrl = normalizeEnvValue((env as any).VITE_API_URL)
  const configured = explicitOrigin || explicitApiBase || legacyApiUrl

  if (configured) {
    if (configured.startsWith('http')) {
      return withoutApiSuffix(configured)
    }

    if (isFrontendDevelopmentMode || isFrontendTestMode) {
      return 'http://localhost:3002'
    }

    // P12-PROD-PATH-R1-M3: same-origin browser derivation. When the app is configured for a
    // same-origin RELATIVE base (e.g. '/api') with no explicit VITE_API_ORIGIN, the canonical
    // absolute origin is exactly where the SPA is running. Narrowly guarded — release-like, in a
    // browser, a single-'/' same-origin path (rejects '//' and '/\' external-origin forms), and a
    // valid http(s) window origin. Every other release-like case stays fail-closed below.
    const sameOriginBase = explicitApiBase || legacyApiUrl
    if (!explicitOrigin && /^\/(?![/\\])/.test(sameOriginBase) && typeof window !== 'undefined') {
      const browserOrigin = normalizeEnvValue(window.location?.origin)
      let parsedOrigin: URL | null = null
      try {
        parsedOrigin = new URL(browserOrigin)
      } catch {
        parsedOrigin = null
      }
      if (parsedOrigin && (parsedOrigin.protocol === 'http:' || parsedOrigin.protocol === 'https:')) {
        return parsedOrigin.origin
      }
    }

    assertFrontendRuntimeConfig(false, 'VITE_API_ORIGIN or absolute VITE_API_BASE_URL is required for non-dev asset/API origin usage')
  }

  if (isFrontendDevelopmentMode || isFrontendTestMode) {
    return 'http://localhost:3002'
  }

  assertFrontendRuntimeConfig(false, 'VITE_API_ORIGIN or VITE_API_BASE_URL is required outside development/test')
}

export function buildApiUrl(path: string): string {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  return `${getApiOriginUrl()}${normalizedPath}`
}

export function getWebSocketUrl(): string {
  const explicitWsUrl = normalizeEnvValue(env.VITE_WS_URL) || normalizeEnvValue((env as any).VITE_WEBSOCKET_URL)

  if (explicitWsUrl) {
    return explicitWsUrl
  }

  if (isFrontendDevelopmentMode || isFrontendTestMode) {
    return 'ws://localhost:3002'
  }

  assertFrontendRuntimeConfig(false, 'VITE_WS_URL is required outside development/test')
}

export function getSocketIoUrl(): string {
  const explicitSocketUrl = normalizeEnvValue((env as any).VITE_SOCKET_IO_URL)

  if (explicitSocketUrl) {
    return explicitSocketUrl
  }

  const wsUrl = getWebSocketUrl()
  return wsUrl.replace(/^wss:\/\//i, 'https://').replace(/^ws:\/\//i, 'http://')
}

export function isPublicDevSurfaceEnabled(): boolean {
  const enabledByFlag = isEnvFlagEnabled((env as any).VITE_ENABLE_PUBLIC_DEV_SURFACES) ||
    isEnvFlagEnabled((env as any).VITE_ENABLE_ROLE_SELECTOR)

  return (isFrontendDevelopmentMode || isFrontendTestMode) && enabledByFlag
}